import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';
import { Sentry } from '@/sentry';

// In development, catch unhandled API promise rejections (module-level setup)
if (import.meta.env.DEV) {
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const error = event.reason;
    if (error && typeof error === 'object' && 'isAxiosError' in error && error.isAxiosError) {
      console.error(
        '⚠️ UNHANDLED API ERROR: Did you forget try/catch with handleError()?\n',
        'URL:', (error as AxiosError).config?.url,
        '\nStatus:', (error as AxiosError).response?.status,
        '\nError:', error
      );
    }
  });
}

// Centralized API service for all backend communication
class ApiService {
  private client: AxiosInstance;

  constructor() {
    const baseURL = import.meta.env.VITE_API_URL;

    // Log API configuration in development/test modes
    if (import.meta.env.DEV || import.meta.env.MODE === 'test') {
      console.log('[ApiService] Initialized with baseURL:', baseURL, '(mode:', import.meta.env.MODE + ')');
    }

    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Include cookies in requests (for JWT tokens)
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

      // Response interceptor - log errors and track with Sentry
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        // Log errors in development
        if (import.meta.env.DEV) {
          console.error('API Error:', {
            url: error.config?.url,
            method: error.config?.method,
            status: error.response?.status,
            data: error.response?.data,
            message: error.message,
          });
        }

          // Track API errors in Sentry (excluding expected errors like 401, 404)
          const status = error.response?.status;
          if (status && status >= 500) {
              Sentry.captureException(error, {
                  tags: {
                      api_endpoint: error.config?.url,
                      api_method: error.config?.method,
                      http_status: status,
                  },
                  extra: {
                      response_data: error.response?.data,
                  },
              });
          }

        return Promise.reject(error);
      }
    );
  }

  async get<T>(endpoint: string): Promise<T> {
    const response = await this.client.get<T>(endpoint);
    return response.data;
  }

  async post<T, R>(endpoint: string, data?: T, config?: { headers?: Record<string, string>, signal?: AbortSignal }): Promise<R> {
    const response = await this.client.post<R>(endpoint, data, config);
    return response.data;
  }

  async put<T, R>(endpoint: string, data?: T): Promise<R> {
    const response = await this.client.put<R>(endpoint, data);
    return response.data;
  }

  async patch<T, R>(endpoint: string, data?: T, config?: { headers?: Record<string, string> }): Promise<R> {
    const response = await this.client.patch<R>(endpoint, data, config);
    return response.data;
  }

  async delete<T>(endpoint: string): Promise<T> {
    const response = await this.client.delete<T>(endpoint);
    return response.data;
  }
}

// Export singleton instance
export const apiService = new ApiService();